// Stock Take types and mock data

export type StockTakeStatus = "Draft" | "Started" | "In Progress" | "Review" | "Complete" | "Completed" | "Scheduled" | "Paused" | "Cancelled";
export type StockTakeType = "Full Stock Take" | "Targeted Count" | "Cycle Count";
export type RecurrenceType = "once" | "daily" | "weekly" | "monthly" | "quarterly" | "annually";

export interface StockTakeItem {
  id: string;
  stockCode: string;
  stockDescription: string;
  location: string;
  systemQuantity: number;
  countedQuantity: number | null;
  variance: number | null;
  notes?: string;
  countedBy?: string;
  countedAt?: Date;
}

export interface StockTake {
  id: string;
  reference: string;
  description: string;
  type: StockTakeType;
  warehouse: string;
  warehouseCode: string;
  status: StockTakeStatus;
  createdBy: string;
  createdAt: Date;
  assignedTo: string;
  startedAt?: Date;
  completedAt?: Date;
  items: StockTakeItem[];
  totalItems: number;
  countedItems: number;
  varianceCount: number;
  notes?: string;
  lastSavedAt?: Date;
  lastSavedBy?: string;
  recurrence?: RecurrenceType;
  nextScheduledDate?: Date;
  isSystemGenerated?: boolean;
  abcClass?: "A" | "B" | "C";
}

// Generate mock stock take items
const generateMockStockTakeItems = (count: number, startIndex: number = 1): StockTakeItem[] => {
  const items: StockTakeItem[] = [];
  const locations = ["A01-R01-S01-B01", "A01-R01-S01-B02", "A01-R01-S02-B01", "B01-R01-S01-B01", "C01-MAIN"];
  
  for (let i = 0; i < count; i++) {
    const index = startIndex + i;
    const systemQty = Math.floor(Math.random() * 200) + 10;
    const hasCount = Math.random() > 0.3; // 70% have been counted
    const countedQty = hasCount ? systemQty + Math.floor(Math.random() * 21) - 10 : null; // ±10 variance
    
    items.push({
      id: `item-${index}`,
      stockCode: `STK${String(index).padStart(3, '0')}`,
      stockDescription: `Stock Item ${index}`,
      location: locations[Math.floor(Math.random() * locations.length)],
      systemQuantity: systemQty,
      countedQuantity: countedQty,
      variance: countedQty !== null ? countedQty - systemQty : null,
      notes: Math.random() > 0.8 ? "Damaged packaging noted" : undefined,
      countedBy: hasCount ? "John Smith" : undefined,
      countedAt: hasCount ? new Date(Date.now() - Math.random() * 86400000) : undefined,
    });
  }
  
  return items;
};

// Mock stock takes
export const mockStockTakes: StockTake[] = [
  // One-time stock takes
  {
    id: "ST-001",
    reference: "ST-2024-001",
    description: "Monthly Stock Take - Warehouse A",
    type: "Full Stock Take",
    warehouse: "Warehouse A",
    warehouseCode: "WH-A",
    status: "In Progress",
    createdBy: "Admin User",
    createdAt: new Date("2024-01-15T09:00:00"),
    assignedTo: "John Smith",
    startedAt: new Date("2024-01-15T09:30:00"),
    items: generateMockStockTakeItems(50, 1),
    totalItems: 50,
    countedItems: 35,
    varianceCount: 8,
    notes: "Focus on high-value items first",
    lastSavedAt: new Date("2024-01-15T14:30:00"),
    lastSavedBy: "John Smith",
    recurrence: "once",
  },
  {
    id: "ST-002",
    reference: "ST-2024-002",
    description: "Cycle Count - Electronics Section",
    type: "Cycle Count",
    warehouse: "Warehouse A",
    warehouseCode: "WH-A",
    status: "Review",
    createdBy: "Admin User",
    createdAt: new Date("2024-01-10T10:00:00"),
    assignedTo: "Jane Doe",
    startedAt: new Date("2024-01-10T10:30:00"),
    items: generateMockStockTakeItems(25, 51),
    totalItems: 25,
    countedItems: 25,
    varianceCount: 3,
    notes: "All items counted, ready for review",
    lastSavedAt: new Date("2024-01-10T16:00:00"),
    lastSavedBy: "Jane Doe",
    recurrence: "once",
  },
  {
    id: "ST-003",
    reference: "ST-2024-003",
    description: "Year-End Stock Take - All Warehouses",
    type: "Full Stock Take",
    warehouse: "Warehouse B",
    warehouseCode: "WH-B",
    status: "Complete",
    createdBy: "Admin User",
    createdAt: new Date("2023-12-28T08:00:00"),
    assignedTo: "Mike Chen",
    startedAt: new Date("2023-12-28T09:00:00"),
    completedAt: new Date("2023-12-29T17:00:00"),
    items: generateMockStockTakeItems(100, 76),
    totalItems: 100,
    countedItems: 100,
    varianceCount: 12,
    notes: "Year-end audit completed successfully",
    lastSavedAt: new Date("2023-12-29T17:00:00"),
    lastSavedBy: "Admin User",
    recurrence: "once",
  },
  {
    id: "ST-004",
    reference: "ST-2024-004",
    description: "Spot Check - Raw Materials",
    type: "Targeted Count",
    warehouse: "Distribution Center",
    warehouseCode: "DC",
    status: "Started",
    createdBy: "Admin User",
    createdAt: new Date("2024-01-16T11:00:00"),
    assignedTo: "Sarah Johnson",
    items: generateMockStockTakeItems(30, 176),
    totalItems: 30,
    countedItems: 0,
    varianceCount: 0,
    notes: "Random sample for quality check",
    lastSavedAt: new Date("2024-01-16T11:00:00"),
    lastSavedBy: "Admin User",
    recurrence: "once",
  },
  {
    id: "ST-005",
    reference: "ST-2024-005",
    description: "Weekly Cycle Count - Retail Store",
    type: "Cycle Count",
    warehouse: "Retail Store",
    warehouseCode: "RS",
    status: "In Progress",
    createdBy: "Store Manager",
    createdAt: new Date("2024-01-14T08:00:00"),
    assignedTo: "Store Manager",
    startedAt: new Date("2024-01-14T08:30:00"),
    items: generateMockStockTakeItems(40, 206),
    totalItems: 40,
    countedItems: 20,
    varianceCount: 2,
    notes: "High-traffic items priority",
    lastSavedAt: new Date("2024-01-14T12:00:00"),
    lastSavedBy: "Store Manager",
    recurrence: "once",
  },
  
  // Scheduled stock takes (System-generated ABC Cycle Counts)
  {
    id: "ST-SCH-001",
    reference: "SCH-2024-001",
    description: "A-Class Cycle Count - All Warehouses",
    type: "Cycle Count",
    warehouse: "",
    warehouseCode: "",
    status: "Scheduled",
    createdBy: "System",
    createdAt: new Date("2024-01-01T00:00:00"),
    assignedTo: "System",
    items: generateMockStockTakeItems(135, 1000).map(item => ({
      ...item,
      countedQuantity: null,
      variance: null,
      countedBy: undefined,
      countedAt: undefined,
    })),
    totalItems: 135,
    countedItems: 0,
    varianceCount: 0,
    notes: "Automated monthly cycle count for A-Class items (high-value) across all warehouses",
    recurrence: "monthly",
    nextScheduledDate: new Date("2024-01-31T23:59:59"),
    isSystemGenerated: true,
    abcClass: "A",
  },
  {
    id: "ST-SCH-002",
    reference: "SCH-2024-002",
    description: "B-Class Cycle Count - All Warehouses",
    type: "Cycle Count",
    warehouse: "",
    warehouseCode: "",
    status: "Scheduled",
    createdBy: "System",
    createdAt: new Date("2024-01-01T00:00:00"),
    assignedTo: "System",
    items: generateMockStockTakeItems(360, 2000).map(item => ({
      ...item,
      countedQuantity: null,
      variance: null,
      countedBy: undefined,
      countedAt: undefined,
    })),
    totalItems: 360,
    countedItems: 0,
    varianceCount: 0,
    notes: "Automated quarterly cycle count for B-Class items (medium-value) across all warehouses",
    recurrence: "quarterly",
    nextScheduledDate: new Date("2024-03-31T23:59:59"),
    isSystemGenerated: true,
    abcClass: "B",
  },
  {
    id: "ST-SCH-003",
    reference: "SCH-2024-003",
    description: "C-Class Cycle Count - All Warehouses",
    type: "Cycle Count",
    warehouse: "",
    warehouseCode: "",
    status: "Scheduled",
    createdBy: "System",
    createdAt: new Date("2024-01-01T00:00:00"),
    assignedTo: "System",
    items: generateMockStockTakeItems(1005, 3000).map(item => ({
      ...item,
      countedQuantity: null,
      variance: null,
      countedBy: undefined,
      countedAt: undefined,
    })),
    totalItems: 1005,
    countedItems: 0,
    varianceCount: 0,
    notes: "Automated annual cycle count for C-Class items (low-value) across all warehouses",
    recurrence: "annually",
    nextScheduledDate: new Date("2024-12-31T23:59:59"),
    isSystemGenerated: true,
    abcClass: "C",
  },
  
  // Other scheduled stock takes
  {
    id: "ST-SCH-004",
    reference: "SCH-2024-004",
    description: "Daily Spot Check - Distribution Center",
    type: "Targeted Count",
    warehouse: "Distribution Center",
    warehouseCode: "DC",
    status: "Scheduled",
    createdBy: "Admin User",
    createdAt: new Date("2024-01-01T00:00:00"),
    assignedTo: "Sarah Johnson",
    items: [],
    totalItems: 20,
    countedItems: 0,
    varianceCount: 0,
    notes: "Daily random spot check of high-turnover items",
    recurrence: "daily",
    nextScheduledDate: new Date("2024-01-17T08:00:00"),
  },
  {
    id: "ST-SCH-005",
    reference: "SCH-2024-005",
    description: "Weekly Location Check - Warehouse A",
    type: "Targeted Count",
    warehouse: "Warehouse A",
    warehouseCode: "WH-A",
    status: "Scheduled",
    createdBy: "Warehouse Manager",
    createdAt: new Date("2024-01-01T00:00:00"),
    assignedTo: "John Smith",
    items: [],
    totalItems: 50,
    countedItems: 0,
    varianceCount: 0,
    notes: "Weekly audit of specific warehouse locations",
    recurrence: "weekly",
    nextScheduledDate: new Date("2024-01-21T09:00:00"),
  },
  {
    id: "ST-SCH-006",
    reference: "SCH-2024-006",
    description: "Quarterly Full Count - Retail Store",
    type: "Full Stock Take",
    warehouse: "Retail Store",
    warehouseCode: "RS",
    status: "Scheduled",
    createdBy: "Store Manager",
    createdAt: new Date("2024-01-01T00:00:00"),
    assignedTo: "Store Manager",
    items: [],
    totalItems: 200,
    countedItems: 0,
    varianceCount: 0,
    notes: "Quarterly full stock count for retail location",
    recurrence: "quarterly",
    nextScheduledDate: new Date("2024-03-31T18:00:00"),
  },
  {
    id: "ST-SCH-007",
    reference: "SCH-2024-007",
    description: "Annual Full Audit - All Locations",
    type: "Full Stock Take",
    warehouse: "",
    warehouseCode: "",
    status: "Scheduled",
    createdBy: "Admin User",
    createdAt: new Date("2024-01-01T00:00:00"),
    assignedTo: "Mike Chen",
    items: [],
    totalItems: 1500,
    countedItems: 0,
    varianceCount: 0,
    notes: "Annual comprehensive stock take across all locations for year-end audit",
    recurrence: "annually",
    nextScheduledDate: new Date("2024-12-31T23:59:59"),
  },
  {
    id: "ST-SCH-008",
    reference: "SCH-2024-008",
    description: "Monthly High-Value Items Check",
    type: "Targeted Count",
    warehouse: "Warehouse B",
    warehouseCode: "WH-B",
    status: "Paused",
    createdBy: "Admin User",
    createdAt: new Date("2024-01-01T00:00:00"),
    assignedTo: "Jane Doe",
    items: [],
    totalItems: 75,
    countedItems: 0,
    varianceCount: 0,
    notes: "Monthly check of high-value items (temporarily paused)",
    recurrence: "monthly",
    nextScheduledDate: new Date("2024-01-31T16:00:00"),
  },
  {
    id: "ST-SCH-009",
    reference: "SCH-2024-009",
    description: "Bi-Weekly Fast-Moving Items",
    type: "Cycle Count",
    warehouse: "Distribution Center",
    warehouseCode: "DC",
    status: "Scheduled",
    createdBy: "DC Manager",
    createdAt: new Date("2024-01-01T00:00:00"),
    assignedTo: "DC Manager",
    items: [],
    totalItems: 60,
    countedItems: 0,
    varianceCount: 0,
    notes: "Bi-weekly count of fast-moving inventory items",
    recurrence: "weekly",
    nextScheduledDate: new Date("2024-01-28T10:00:00"),
  },
];

// Helper function to calculate progress percentage
export const calculateProgress = (stockTake: StockTake): number => {
  if (stockTake.totalItems === 0) return 0;
  return Math.round((stockTake.countedItems / stockTake.totalItems) * 100);
};

// Helper function to get status color
export const getStatusColor = (status: StockTakeStatus): string => {
  switch (status) {
    case "Draft":
      return "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400";
    case "Started":
      return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400";
    case "In Progress":
      return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400";
    case "Review":
      return "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400";
    case "Complete":
    case "Completed":
      return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
    case "Scheduled":
      return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400";
    case "Paused":
      return "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400";
    case "Cancelled":
      return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400";
    default:
      return "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400";
  }
};

// Helper function to get type color
export const getTypeColor = (type: StockTakeType): string => {
  switch (type) {
    case "Full Stock Take":
      return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400";
    case "Targeted Count":
      return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
    case "Cycle Count":
      return "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400";
    default:
      return "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400";
  }
};

// Helper function to format recurrence
export const formatRecurrence = (recurrence?: RecurrenceType): string => {
  if (!recurrence || recurrence === "once") return "One-time";
  return recurrence.charAt(0).toUpperCase() + recurrence.slice(1);
};

// Helper function to get next status
export const getNextStatus = (currentStatus: StockTakeStatus): StockTakeStatus | null => {
  switch (currentStatus) {
    case "Started":
      return "In Progress";
    case "In Progress":
      return "Review";
    case "Review":
      return "Complete";
    case "Complete":
      return null;
    case "Scheduled":
      return "Started";
    case "Paused":
      return "Scheduled";
    default:
      return null;
  }
};

// Helper function to get previous status
export const getPreviousStatus = (currentStatus: StockTakeStatus): StockTakeStatus | null => {
  switch (currentStatus) {
    case "In Progress":
      return "Started";
    case "Review":
      return "In Progress";
    case "Complete":
      return "Review";
    case "Started":
      return "Scheduled";
    case "Scheduled":
      return "Paused";
    default:
      return null;
  }
};
